import * as Utils from '@js/modules/utils';
import I18n from '@js/modules/translations';

import MapRequest from '@js/modules/maps/mapRequest';
import MapInterface from '@js/modules/maps/mapInterface';

import Polyline from '@js/modules/maps/polyline';
import Simplify from '@js/modules/maps/simplify';

import markerStepIcon from '@/images/maps/marker-step.png';
import markerPauseIcon from '@/images/maps/marker-pause-edit.png';
import markerStartIcon from '@/images/maps/marker-start-play.png';
import markerDistanceIcon from '@/images/maps/distance-marker.png';

// import PWAManager from '@js/modules/pwaManager';


export default class BuildMap extends MapInterface {
    _mapModel = {
        // Initial map points
        initialPoints: undefined,

        // Not used for now
        // // activity polyline
        // activityPolylines: [],

        // activity points
        linePoints: [],

        // all direction arrows
        directionArrows: [],

        // all ghost points
        ghostPoints: [],

        // all distance Markers
        distanceMarkers: [],

        // Marker ID currently dragging
        draggingMarkerId: null,

        // Prevent double click on marker and on map at the same time
        markerEventClicked: false,

        // undo actions
        snapShotStack: [],

        // If a pop-up is already opened
        hasPopup: false,

        // Editing ride ID
        rideId: undefined,

        // Edition mode (can add points and lines)
        editMode: true,

        // callback functions for edit
        mapEdited: [],

        // callback functions for reset
        mapReset: [],

        // distance for callback functions (in km)
        distance: 0,

        // Slope color mode
        slopePercentageEnabled: false,

        // Surface color mode
        surfaceTypeEnabled: false,

        // view point for checking a specific point on map (ex. chart hover)
        viewPointMarker: undefined,

        // OPTIMIZE: utility?
        // // Back to home
        // isReturnedToHome: false,

        // Distance updated
        distanceUpdated: undefined,

        // Start point changed
        startPointChanged: undefined,

        // Total duration
        durationSeconds: 0
    };

    constructor(modules, params) {
        params.mapMarkers = [
            {
                id: 'ghost-marker',
                url: markerStepIcon,
                properties: {
                    opacity: 0,
                    size: 0.5
                }
            },
            {
                id: 'step-marker',
                url: markerStepIcon,
                properties: {
                    size: 0.7
                }
            },
            {
                id: 'pause-marker',
                url: markerPauseIcon,
                properties: {
                    size: 0.7
                }
            },
            // {
            //     id: 'distance-marker',
            //     url: `${window.settings.assets_url}/maps/distance-marker.png`,
            //     properties: {
            //         size: 1,
            //         opacity: 1
            //     }
            // },
            {
                id: 'start-marker',
                url: markerStartIcon,
                properties: {
                    size: 0.5
                }
            }
        ];

        super(modules, params);

        if (this.hasModule('HELPER') && this.plugHelperEvents) {
            this.plugHelperEvents();
        }

        let isRideSharedOrCopied = null;

        if (params.initialPoints) {
            this._mapModel.initialPoints = params.initialPoints;

            if (this.hasModule('RIDE_STORE') && this.storeRide) {
                this.storeRide();
            }
        } else {
            if (this.hasModule('RIDE_SHARE') && this.getSharedRide) {
                isRideSharedOrCopied = this.getSharedRide((mode, rideName) => {
                    if (mode === 'share') {
                        this.displayMapHelper(I18n.t('js.maps.helpers.shared_ride', {name: rideName}));
                    } else if (mode === 'copied') {
                        this.displayMapHelper(I18n.t('js.maps.helpers.copied_ride', {name: rideName}));
                    }
                });
            }

            if (!isRideSharedOrCopied && params.reusePreviousTracks && this.hasModule('RIDE_STORE') && this.getStoredRide) {
                this._mapModel.initialPoints = this.getStoredRide();

                if (this._mapModel.initialPoints && this._mapModel.initialPoints.length > 0) {
                    if (this.hasModule('HELPER') && this.plugHelperEvents) {
                        this.displayMapHelper(I18n.t('js.maps.helpers.stored_ride'));
                    }
                }
            }
        }

        if (Utils.isPresent(params.editMode)) {
            this._mapModel.editMode = params.editMode;
        }

        if (Utils.isPresent(params.rideId)) {
            this._mapModel.rideId = params.rideId;
        }

        this._mapModel.$styleButtonContainers = this._model.$mapId.querySelector('#map-controls-container');

        if (this.hasModule('TOOLBAR') && this.initializeToolbar) {
            this.initializeToolbar();
            if ((!this._mapModel.initialPoints || this._mapModel.initialPoints.length === 0) && !isRideSharedOrCopied) {
                this.disableToolbarButtons();
            }
        }

        this.onMapLoaded(() => {
            this.addMapTrackLine(this._model.mapTrackLineName);

            this._mapInitialSetup();

            if (this.hasModule('ELEVATION_COMPUTATION') && this.createElevationMap) {
                this.createElevationMap();
            }

            if (this._mapModel.editMode) {
                this.onMapEdited(() => {
                    this._drawDirectionArrows();
                    // this._drawGhostPoints();
                });
            }

            if (params.highlightedPlaceName) {
                this.highlightPlaceName(params.initialLocation[0], params.initialLocation[1], params.highlightedPlaceName);
            } else if (params.useSearchMarker && params.initialLocation) {
                this.displaySearchMarker(params.initialLocation[0], params.initialLocation[1]);
            }

            if (this.hasModule('HELPER') && this.plugHelperEvents) {
                this.displayMapHelper(window.screen.width < 1024
                        ?
                        I18n.t('js.maps.helpers.mobile.first_point')
                        :
                        I18n.t('js.maps.helpers.desktop.first_point')
                    , {limitDisplay: true});
            }

            if (this._mapModel.rideId && this.hasModule('RIDE_POIS') && this.fetchRidePOIs) {
                this.fetchRidePOIs(this._mapModel.rideId);
            }

            this._model.map.on('zoomstart', () => {
                this._model.previousZoom = this._model.map.getZoom();
            });

            this._model.map.on('zoomend', () => {
                this._saveMapPosition();

                if (!this.isDataChangedOnZoom(this._model.map.getZoom(), this._model.previousZoom)) {
                    return;
                }

                this._redrawKeyPoints({refresh: true});
                this._drawDirectionArrows();
            });

            if (this._mapModel.editMode) {
                // Don't add point if double click or double tapping
                this._model.map.doubleClickZoom.enable();

                // Add a listener for the click event
                this._model.map.on('click', this._addKeyPointDebounce);

                this._model.map.on('dblclick', () => this._model.isDoubleClick = true);

                this._addKeyPointEventHandlers();
            }
        });
    }

    goToLocation = (bounds, center, name, {
        useStartMarker = false,
        showPositionMarker = false
    } = {}) => {
        if (!this._model.map) {
            return;
        }

        // Use first bounds then center
        let lat;
        let lng;
        bounds = this._buildBounds(bounds);

        if (Utils.is().isObject(center)) {
            lat = center.lat;
            lng = center.lng;
        } else {
            lat = center[1];
            lng = center[0];
        }

        if ((!lat || !lng) && !bounds) {
            return;
        }

        // Use search marker as starting point
        if (useStartMarker && !this._model.isComputingRoad && !this._mapModel.linePoints.length) {
            if (this.hasModule('TOOLBAR') && this.activateToolbarButtons) {
                this.activateToolbarButtons();
            }

            if (this.hasModule('ISOCHRONE') && this.computeIsochrone) {
                this.computeIsochrone([lat, lng]);
            }

            this._pushUndoSnapShot();

            this._mapModel.linePoints.push(new MapInterface.PointInfo({
                lat,
                lng
            }, null));

            this._redrawKeyPoints();
            this._mapEditedFuncs();

            // this._model.startingPointAddress = this._model.$locationSearch.val().split(', ').slice(0, 2).join(', ');
        } else if (name) {
            this.highlightPlaceName(lat, lng, name);
        } else if (showPositionMarker) {
            this.displaySearchMarker(lat, lng);
        }

        if ((bounds && (bounds._sw || bounds._ne)) || Array.isArray(bounds)) {
            this.fitBounds(bounds, {padding: 50});
        } else if (center) {
            this.flyTo({
                lat,
                lng
            }, useStartMarker ? 16 : this._model.searchZoom);
        }
    };

    onDistanceUpdated = (callback) => {
        this._mapModel.distanceUpdated = callback;
    };

    onStartPointChanged = (callback) => {
        this._mapModel.startPointChanged = callback;
    };

    onMapEdited = (callback) => {
        this._mapModel.mapEdited.push(callback);
    };

    backToHome = () => {
        if (this._model.isComputingRoad || this._mapModel.linePoints.length < 2) {
            return;
        }

        this._pushUndoSnapShot();

        const firstLinePointCoordinates = this._mapModel.linePoints[0].latLng;

        this._addKeyPointAtClickLoc(firstLinePointCoordinates);

        if (this.hasModule('RIDE_STORE') && this.storeRide) {
            this.storeRide(this._mapModel.linePoints);
        }

        if (this.hasModule('TOOLBAR')) {
            this.disableBackToHome();
        }
    };

    getLinePoints = () => {
        return this._mapModel.linePoints;
    };

    getElevationData = () => {
        if (!this._mapModel.linePoints.length) return null;

        let totalDistance = 0;
        const points = [];

        for (const p of this._mapModel.linePoints) {
            if (p.elevation === null || p.elevation === undefined) continue;

            totalDistance += p.deltaDistance || 0;
            points.push({
                distance: totalDistance / 1000,
                elevation: p.elevation,
                lat: p.latLng.lat,
                lng: p.latLng.lng
            });
        }

        return points.length ? points : null;
    };

    hasActivityData = () => {
        return this._mapModel.linePoints.length > 0;
    };

    hasTimeData = () => {
        return this._mapModel.linePoints.some((point) => point.datetime);
    };

    hasOnlyOnePoint = () => {
        return this._mapModel.linePoints.length === 1;
    };

    undo = () => {
        if (this._mapModel.snapShotStack.length !== 0) {
            this._revertToSnapshot();
        } else {
            // If no snapshot, it means it's a reused route. So clear the route.
            this.clear();
        }

        if (this._mapModel.distanceUpdated) {
            this._mapModel.distanceUpdated(this._mapModel.distance);
        }

        if (this.hasModule('TOOLBAR') && !this.hasActivityData()) {
            this.disableToolbarButtons();
        }

        if (this.hasModule('RIDE_STORE') && this.storeRide) {
            this.storeRide(this._mapModel.linePoints);
        }
    };

    clearMap = (callback, {noPopup = false} = {}) => {
        if (!this.hasActivityData()) {
            return;
        }

        if (this.alertPopup && !noPopup) {
            this.alertPopup.fire({
                title: I18n.t('js.maps.clear_map.title'),
                // text: I18n.t('js.maps.clear_map.text'),
                icon: 'warning',
                showCancelButton: true,
                cancelButtonText: I18n.t('js.helpers.buttons.cancel'),
                confirmButtonText: I18n.t('js.maps.clear_map.confirm')
            })
                .then((result) => {
                    if (result.isConfirmed) {
                        // this.reset();
                        this.clear();
                        this._redrawKeyPoints();
                        this._mapEditedFuncs();

                        if (this.hasModule('TOOLBAR')) {
                            this.disableToolbarButtons();
                        }

                        if (this.hasModule('ISOCHRONE')) {
                            this.clearIsochrone();
                        }

                        if (typeof callback === 'function') {
                            callback();
                        }
                    }
                });
        } else {
            this.clear();
            this._redrawKeyPoints();
            this._mapEditedFuncs();

            if (this.hasModule('TOOLBAR')) {
                this.disableToolbarButtons();
            }

            if (this.hasModule('ISOCHRONE')) {
                this.clearIsochrone();
            }

            if (this.hasModule('ELEVATION_COMPUTATION')) {
                this.clearElevationMap();
            }

            if (typeof callback === 'function') {
                callback();
            }
        }
    };

    reset = (action) => {
        if (!Utils.isPresent(this._model.map)) {
            return;
        }

        // just undo everything.
        if (this._mapModel.snapShotStack.length > 0) {
            this._revertToSnapshot(0);
        }

        if (this._mapModel.mapReset.length > 0) {
            for (var i = this._mapModel.mapReset.length - 1; i >= 0; i--) {
                this._mapModel.mapReset[i]();
            }
        }

        this._mapModel.mapReset = [];

        // Call edited callbacks
        if (!(action && action === 'clear')) {
            this._mapEditedFuncs();
        }
        if (this._mapModel.distanceUpdated) {
            this._mapModel.distanceUpdated(this._mapModel.distance);
        }

        if (this._mapModel.startPointChanged) {
            this._mapModel.startPointChanged(null);
        }

        if (this.hasModule('RIDE_STORE') && this.storeRide) {
            this.storeRide(this._mapModel.linePoints);
        }
    };

    clear = () => {
        if (!Utils.isPresent(this._model.map)) {
            return;
        }

        // kill all the things
        this.reset('clear');
        this._clearKeyPoints({clearAll: true});
        this._removeDistanceMarkers();

        this.resetMapSource(this._model.mapTrackLineName);

        // this._mapModel.activityPolylines = [];
        this._model.previousRoadComputedPoints = 0;
        this._mapModel.keyPoints = [];
        this._mapModel.linePoints = [];
        this._mapModel.distanceMarkers = [];
        this._mapModel.distance = 0;
        if (this._mapModel.surfaceTypeEnabled) {
            this._mapModel.surfaceTypeEnabled = false;
            this.setSurfaceTypeMode(this._model.mapTrackLineName, false);
        }
        this.removeSurfaceLayers();
        this._mapModel.draggingMarkerId = null;
        // OPTIMIZE: utility?
        // this._mapModel.isReturnedToHome = false;
        if (this._mapModel.distanceUpdated) {
            this._mapModel.distanceUpdated(this._mapModel.distance);
        }

        if (this._mapModel.startPointChanged) {
            this._mapModel.startPointChanged(null);
        }

        if (this.hasModule('RIDE_STORE') && this.storeRide) {
            this.storeRide();
        }

        if (this.hasModule('ISOCHRONE')) {
            this.clearIsochrone();
        }

        if (this.hasModule('ELEVATION_COMPUTATION')) {
            this.clearElevationMap();
        }
    };

    clearSavedData = () => {
        if (this.hasModule('RIDE_STORE') && this.storeRide) {
            this.storeRide();
        }
    };

    simplifyLine = (coordinates, {
        project = false,
        isAdaptiveTolerance = false,
        defaultTolerance = 2.0,
        convertToXY = false,
        highPrecision = true
    } = {}) => {
        let fullPoints = [];
        const polylinesLength = coordinates.length;
        const tolerance = isAdaptiveTolerance ? (polylinesLength > 200 ? Math.round(polylinesLength / 50) : defaultTolerance) : defaultTolerance;

        if (project) {
            for (let coordinateIndex = 0; coordinateIndex < coordinates.length; coordinateIndex++) {
                fullPoints.push(this._model.map.project(coordinates[coordinateIndex]));
            }
        } else if (convertToXY) {
            fullPoints = coordinates.map((point) => ({
                x: point[0],
                y: point[1]
            }));
        } else {
            fullPoints = coordinates;
        }

        const simplifiedPoints = Simplify(fullPoints, tolerance, highPrecision);

        let finalPoints = [];
        if (project) {
            for (var pointIndex = 0; pointIndex < simplifiedPoints.length; pointIndex++) {
                var latLng = this._model.map.unproject(simplifiedPoints[pointIndex]);
                finalPoints.push([latLng.lat, latLng.lng]);
            }
        } else if (convertToXY) {
            finalPoints = simplifiedPoints.map((point) => [point.x, point.y]);
        } else {
            finalPoints = simplifiedPoints;
        }

        return finalPoints;
    };

    staticMapUrl = () => {
        var overlay;
        var size = [600, 500];
        var padding = 100;
        var strokeWidth = 5;
        var lineOpacity = 0.85;
        var mapPosition = 'auto';

        if (Utils.isPresent(this._mapModel.linePoints)) {
            var coordinates = this._mapModel.linePoints.map((linePoint) => [linePoint.latLng.lng, linePoint.latLng.lat]);

            var simplifiedPolyline = Polyline.encode(this.simplifyLine(coordinates, {
                project: true,
                isAdaptiveTolerance: true
            }));

            overlay = 'path-' + strokeWidth + '+' + this._model.mapTrackLineColor.substring(1) + '-' + lineOpacity + '(' + encodeURIComponent(simplifiedPolyline) + ')';
        } else {
            return null;
        }

        return MapRequest.mapboxStaticMapUrl + '/' +
            overlay + '/' +
            mapPosition + '/' +
            size.join('x') +
            '?padding=' + padding +
            '&attribution=false&logo=false' +
            '&access_token=' + window.settings.mapbox_key;
    };

    serializePoints = () => {
        // output format:
        var serializeString = '';

        for (var i = 0; i < this._mapModel.linePoints.length; i++) {
            var currentLinePoint = this._mapModel.linePoints[i];
            var deltaDistance = 0;
            var deltaTime = currentLinePoint.deltaTime;
            var prevLinePoint;
            if (i !== 0) {
                prevLinePoint = this._mapModel.linePoints[i - 1];
                if (prevLinePoint.deltaPause === 0) {
                    deltaDistance = currentLinePoint.deltaDistance || this.distHaversine(this._mapModel.linePoints[i - 1].latLng, currentLinePoint.latLng, MapInterface.COORDINATES_PRECISION);
                }

                if (currentLinePoint.deltaTime === 0 && this._mapModel.durationSeconds !== 0) {
                    deltaTime = this._mapModel.durationSeconds / (this._mapModel.linePoints.length - 1);
                }
            }

            var pointInfoType;
            if (i === 0) {
                pointInfoType = 'start';
            } else if (i === this._mapModel.linePoints.length - 1) {
                pointInfoType = 'end';
            } else if (currentLinePoint.deltaPause > 0) {
                pointInfoType = 'resume';
            } else if (this._mapModel.linePoints[i + 1].deltaPause > 0) {
                pointInfoType = 'pause';
            } else if (currentLinePoint.computed) {
                pointInfoType = 'auto';
            } else {
                pointInfoType = 'manual';
            }

            // Serialized structure (from PointInfo):
            // 0 : point type (start, manual, auto, resume, pause, end)
            // 1 : latitude
            // 2 : longitude
            // 3 : elevation
            // 4 : delta distance with previous point
            // 5 : delta time with previous point
            // 6 : delta pause with previous point
            // 7 : datetime / timestamp
            // 8 : speed
            // 9 : heart rate

            if (currentLinePoint.heartRate || currentLinePoint.speed || currentLinePoint.datetime || currentLinePoint.deltaPause) {
                serializeString += [
                    pointInfoType,
                    parseFloat(currentLinePoint.latLng.lat.toFixed(MapInterface.COORDINATES_PRECISION)),
                    parseFloat(currentLinePoint.latLng.lng.toFixed(MapInterface.COORDINATES_PRECISION)),
                    currentLinePoint.elevation,
                    parseFloat(deltaDistance.toFixed(3)),
                    deltaTime,
                    currentLinePoint.deltaPause,
                    currentLinePoint.datetime,
                    currentLinePoint.speed,
                    currentLinePoint.heartRate
                ].join(',') + ';';
            } else {
                serializeString += [
                    pointInfoType,
                    parseFloat(currentLinePoint.latLng.lat.toFixed(MapInterface.COORDINATES_PRECISION)),
                    parseFloat(currentLinePoint.latLng.lng.toFixed(MapInterface.COORDINATES_PRECISION)),
                    currentLinePoint.elevation,
                    parseFloat(deltaDistance.toFixed(3))
                ].join(',') + ';';
            }
        }

        return serializeString;
    };

    updatePointsWithElevation = (elevationPoints) => {
        if (elevationPoints.length !== this._mapModel.linePoints.length) {
            return;
        }

        for (var i = 0; i < this._mapModel.linePoints.length; i++) {
            var currentLinePoint = this._mapModel.linePoints[i];

            if (elevationPoints[i] && (elevationPoints[i].elevation || elevationPoints[i].elevation === 0)) {
                if (currentLinePoint.latLng.lat.toFixed(MapInterface.COORDINATES_PRECISION) === elevationPoints[i].lat.toFixed(MapInterface.COORDINATES_PRECISION) && currentLinePoint.latLng.lng.toFixed(MapInterface.COORDINATES_PRECISION) === elevationPoints[i].lng.toFixed(MapInterface.COORDINATES_PRECISION)) {
                    currentLinePoint.elevation = elevationPoints[i].elevation;
                }
            }
        }
    };

    toggleSlopePercentage = (enabled, coordinateElevations) => {
        this._mapModel.slopePercentageEnabled = enabled;

        if (enabled) {
            // Disable surface mode if active
            if (this._mapModel.surfaceTypeEnabled) {
                this._mapModel.surfaceTypeEnabled = false;
                this.setSurfaceTypeMode(this._model.mapTrackLineName, false);
            }

            if (coordinateElevations?.length) {
                const slopeGeoJSON = this.buildSlopeGeoJSON(coordinateElevations);
                this.updateSource(this._model.mapTrackLineName, slopeGeoJSON);
                this.setSlopePercentageMode(this._model.mapTrackLineName, true);
            }
        } else {
            this.setSlopePercentageMode(this._model.mapTrackLineName, false);
            this._redrawTripLine();
        }
    };

    applySlopePercentageIfEnabled = (coordinateElevations) => {
        if (!this._mapModel.slopePercentageEnabled || !coordinateElevations?.length) {
            return;
        }

        const slopeGeoJSON = this.buildSlopeGeoJSON(coordinateElevations);
        this.updateSource(this._model.mapTrackLineName, slopeGeoJSON);
        this.setSlopePercentageMode(this._model.mapTrackLineName, true);
    };

    toggleSurfaceType = (enabled) => {
        this._mapModel.surfaceTypeEnabled = enabled;

        if (enabled) {
            // Disable slope mode if active
            if (this._mapModel.slopePercentageEnabled) {
                this._mapModel.slopePercentageEnabled = false;
                this.setSlopePercentageMode(this._model.mapTrackLineName, false);
            }

            this.applySurfaceTypeIfEnabled();
        } else {
            this.setSurfaceTypeMode(this._model.mapTrackLineName, false);
            this._redrawTripLine();
        }
    };

    applySurfaceTypeIfEnabled = () => {
        if (!this._mapModel.surfaceTypeEnabled || !this._mapModel.linePoints?.length) {
            return;
        }

        const surfaceGeoJSON = this.buildSurfaceGeoJSON(this._mapModel.linePoints);
        this.setSurfaceTypeMode(this._model.mapTrackLineName, true, surfaceGeoJSON);
    };

    importTrack = (type, importedTracks, callback) => {
        const importTrackCallback = (newTrackData, isCombined, importCallback) => {
            this.clear();

            if (isCombined) {
                this._mapModel.initialPoints = this._mapModel.initialPoints.concat(newTrackData);
            } else {
                this._mapModel.initialPoints = newTrackData;
            }

            this._model.mapBounds = null;

            this._setupPoints();
            this._redrawKeyPoints();
            this._redrawTripLine();
            this._fitMapBounds();
            if (this.hasModule('TOOLBAR') && this.activateToolbarButtons) {
                this.activateToolbarButtons();
            }

            if (importCallback) {
                importCallback(isCombined, this._mapModel.distance);
            }
        };

        if (type === 'ride' && importedTracks) {
            if (this._mapModel.linePoints.length > 0) {
                this.alertPopup.fire({
                    icon: 'warning',
                    showDenyButton: true,
                    showCancelButton: true,
                    title: I18n.t('js.maps.import.title'),
                    confirmButtonText: I18n.t('js.maps.import.replace'),
                    denyButtonText: I18n.t('js.maps.import.combine'),
                    cancelButtonText: I18n.t('js.helpers.buttons.cancel'),
                })
                    .then((result) => {
                        if (result.isConfirmed) {
                            // Replace current track
                            importTrackCallback(importedTracks, false, callback);
                        } else if (result.isDenied) {
                            // Combine with current track
                            importTrackCallback(importedTracks, true, callback);
                        }
                    });
            } else {
                importTrackCallback(importedTracks, false, callback);
            }
        }
    };

    /* Private Methods
    ******************** */
    _mapInitialSetup = () => {
        if (Utils.isPresent(this._mapModel.initialPoints)) {
            // establish key points to edit with
            this._setupPoints();

            // // draw trip polyline, add to map
            // this._redrawTripLine();

            this._fitMapBounds();
        }
    };

    _setupPoints = () => {
        var i;

        this._mapModel.linePoints = [];

        // Draw polylines points
        if (this._mapModel.initialPoints.length > 0) {
            // var j = -1;
            for (i = 0; i < this._mapModel.initialPoints.length; i++) {
                if (this._mapModel.initialPoints[i].type === 'PoiPoint') {
                    continue;
                    // } else {
                    //     j++;
                }

                var computed = Utils.isPresent(this._mapModel.initialPoints[i].computed) || this._mapModel.initialPoints[i].type === 'auto';
                var deltaDistance = Utils.isEmpty(this._mapModel.initialPoints[i].deltaDistance) ? 0 : this._mapModel.initialPoints[i].deltaDistance;
                var deltaTime = Utils.isEmpty(this._mapModel.initialPoints[i].deltaTime) ? 0 : this._mapModel.initialPoints[i].deltaTime;
                var deltaPause = Utils.isEmpty(this._mapModel.initialPoints[i].deltaPause) ? 0 : this._mapModel.initialPoints[i].deltaPause;
                var datetime = this._mapModel.initialPoints[i].datetime;
                var elevation = this._mapModel.initialPoints[i].elevation;
                var speed = this._mapModel.initialPoints[i].speed;
                var heartRate = this._mapModel.initialPoints[i].heartRate;
                this._mapModel.linePoints.push(new MapInterface.PointInfo(
                    {
                        lat: this._mapModel.initialPoints[i].latitude,
                        lng: this._mapModel.initialPoints[i].longitude
                    },
                    null, // keyPoint
                    {
                        computed: computed, // i !== 0 || i !== this._mapModel.initialPoints.length - 1,
                        deltaDistance: deltaDistance,
                        deltaTime: deltaTime,
                        deltaPause: deltaPause,
                        datetime: datetime,
                        elevation: elevation,
                        speed: speed,
                        heartRate: heartRate
                    }
                ));
            }
        }

        // if we're in edit mode, setup key points with events
        this._redrawKeyPoints();
    };

    // OPTIMIZE: utility?
    // _addMarkersPointEventHandlers = (markerPoint, markerInfo, markerIndex) => {
    //     markerPoint.marker.getElement()
    //         .addEventListener('click', () => {
    //             if (this._model.searchMarker) {
    //                 this._model.searchMarker.remove();
    //             }
    //         });
    //
    //     markerPoint.marker.getElement()
    //         .addEventListener('contextmenu', () => {
    //             this._mapEditedFuncs();
    //         });
    // };

    _redrawKeyPoints = (options) => {
        options ||= {};

        // clear key points
        this._clearKeyPoints();

        var thisLinePoint;
        var markerName;
        var mapMarkers = {};
        var stepMarkerInterval = this.getZoomDictionary(this._model.map.getZoom()).stepDist;

        // loop through, if the point should be a keypoint, set the keypoint variable and add to map
        var distanceSinceLastKeyPoint = 0;
        var distanceWithEndKeyPoint;
        for (var linePointIndex = 0; linePointIndex < this._mapModel.linePoints.length; linePointIndex++) {
            thisLinePoint = this._mapModel.linePoints[linePointIndex];

            if (linePointIndex === 0) {
                if (options.refresh) {
                    continue;
                }

                // set start point options
                markerName = 'start-marker';
            } else if (linePointIndex === this._mapModel.linePoints.length - 1) {
                if (options.refresh) {
                    continue;
                }

                // set finish point options
                markerName = 'finish-marker';
            } else if (this._mapModel.linePoints[linePointIndex + 1].deltaPause > 0) {
                // set pause point options
                markerName = 'pause-marker';
            } else if (thisLinePoint.deltaPause > 0) {
                // set resume point options
                markerName = 'resume-marker';
            } else if (thisLinePoint.computed) {
                // if point is computed, display points accordingly to the current zoom
                var prevLineComputedPoint = this._mapModel.linePoints[linePointIndex - 1];
                distanceSinceLastKeyPoint += this.distHaversine(prevLineComputedPoint.latLng, thisLinePoint.latLng, MapInterface.COORDINATES_PRECISION);
                distanceWithEndKeyPoint = this.distHaversine(thisLinePoint.latLng, this._mapModel.linePoints[this._mapModel.linePoints.length - 1].latLng, MapInterface.COORDINATES_PRECISION);

                // if more than X meters have passed since last key point, make a normal key point.
                // otherwise, simply kick the distance since last key point variable
                if (stepMarkerInterval && distanceSinceLastKeyPoint > stepMarkerInterval && distanceWithEndKeyPoint > 100) {
                    markerName = 'step-marker';
                    distanceSinceLastKeyPoint = 0;
                } else {
                    markerName = null;
                }
            } else {
                // normal point need to be present when dragging the siblings points
                markerName = 'step-marker';
            }

            if (markerName && thisLinePoint.latLng) {
                thisLinePoint.keyPoint = {
                    type: 'Feature',
                    properties: {
                        markerId: markerName,
                        linePointIndex: linePointIndex,
                        draggable: this._mapModel.editMode
                    },
                    geometry: {
                        type: 'Point',
                        coordinates: [thisLinePoint.latLng.lng, thisLinePoint.latLng.lat]
                    }
                };
                mapMarkers[markerName] = (mapMarkers[markerName] || []).concat(thisLinePoint.keyPoint);
            }
        }

        Object.keys(mapMarkers)
            .forEach((markerType) => {
                this.updateSource(markerType, {
                    type: 'FeatureCollection',
                    features: mapMarkers[markerType]
                });
            });

        if (this._mapModel.linePoints.length > 1 && !options.refresh) {
            this._redrawTripLine();
        }
    };

    _redrawTripLine = () => {
        let linePointIndex;

        // During an active marker drag this runs on every mousemove. Skip the distance-marker
        // DOM work (removing then re-creating maplibre Markers) to avoid per-frame churn: the
        // existing markers stay in place and are fully rebuilt once when the drag ends.
        const isDragging = !!this._mapModel.draggingMarkerId;

        // remove markers
        if (!isDragging) {
            this._removeDistanceMarkers();
        }

        // clear pointsByDistanceTraveled object (used by highCharts)
        this._model.pointsByDistanceTraveled = {};

        // set initial 'total Distance' variable
        let totalDistance = 0;

        // Reset to default colors while redrawing (slope/surface colors will be re-applied after)
        if (this._mapModel.slopePercentageEnabled) {
            this.setSlopePercentageMode(this._model.mapTrackLineName, false);
        }
        if (this._mapModel.surfaceTypeEnabled) {
            this.setSurfaceTypeMode(this._model.mapTrackLineName, false);
        }

        // remove all activity line polylines from the map
        this.resetMapSource(this._model.mapTrackLineName);

        // // clear both arrays for re-use
        // this._mapModel.activityPolylines = [];

        // distance variables for lines
        let lastLineStartPointIndex = 0;
        let lastLinePointTotalDistance = 0;

        // what distance marker we are currently on
        let markerCount = 0;

        // Only trust the cached per-point deltaDistance (authoritative source distance from an
        // imported/shared/saved ride) while the track is pristine. As soon as the user edits,
        // points get spliced/deleted and a point's previous neighbour may change, leaving its
        // cached delta stale; from then on we recompute every leg geometrically.
        const trustCachedDeltas = this._mapModel.snapShotStack.length === 0;

        const mapZoom = this._model.map.getZoom();
        for (linePointIndex = 0; linePointIndex < this._mapModel.linePoints.length; linePointIndex++) {
            let thisLegDist = 0;

            // if we have reached a pause point OR the last point (finish) draw this segment and process
            if (linePointIndex === this._mapModel.linePoints.length - 1 /* || this._mapModel.linePoints[linePointIndex + 1].deltaPause > 0 */) {
                // draw this line segment
                const nextLine = [];
                for (let j = lastLineStartPointIndex; j <= linePointIndex; j++) {
                    const thisLinePointLatLng = this._mapModel.linePoints[j].latLng;
                    let prevLinePointLatLng;

                    nextLine.push(this._mapModel.linePoints[j].latLng);

                    if (j !== lastLineStartPointIndex) {
                        prevLinePointLatLng = this._mapModel.linePoints[j - 1].latLng;

                        if (trustCachedDeltas && this._mapModel.linePoints[j].deltaDistance) {
                            thisLegDist = this._mapModel.linePoints[j].deltaDistance;
                            totalDistance += thisLegDist;
                        } else {
                            thisLegDist = this.distHaversine(prevLinePointLatLng, thisLinePointLatLng, MapInterface.COORDINATES_PRECISION);
                            this._mapModel.linePoints[j].deltaDistance = thisLegDist;
                            totalDistance += thisLegDist;
                        }
                    }

                    // Check if max distance is reached
                    // OPTIMIZE: for edit mode
                    // if (this._mapModel.mode !== localMap.mode.EDIT) {
                    const isBike = this._model.routingRouteType === 'bike';
                    const mapMaxDistance = isBike ? this._model.mapMaxDistance * 2 : this._model.mapMaxDistance;
                    if (totalDistance >= mapMaxDistance) {
                        // if (this.alertPopup) {
                        //     const hasOnlyPoint = this._mapModel.linePoints.length === 1;
                        //     this.alertPopup.fire({
                        //         title: I18n.t('js.maps.errors.distance.title'),
                        //         text: I18n.t('js.maps.errors.distance.text', {distance: parseInt(mapMaxDistance) / 1000}),
                        //         confirmButtonText: I18n.t('js.maps.errors.distance.remove_last_point'),
                        //         icon: 'warning',
                        //         showDenyButton: hasOnlyPoint,
                        //         denyButtonText: I18n.t('js.maps.errors.distance.remove_first_point'),
                        //     })
                        //         .then(function (event) {
                        //             if (event.isConfirmed) {
                        //                 // Remove last point
                        //                 this.undo();
                        //             } else if (event.isDenied) {
                        //                 this.clear();
                        //             }
                        //         }.bind(this));
                        // }

                        this.undo();

                        this.leaveFullscreen();

                        if (this._model.showProductModal) {
                            this._model.showProductModal('map_max_distance');
                        }

                        return;
                    }

                    // }

                    // handle the total distance stamp for Highcharts point mapping
                    this._model.pointsByDistanceTraveled[Math.floor(totalDistance)] = thisLinePointLatLng;

                    // Are we 1 distance unit (or more) greater? try to draw a distance marker
                    const curDist = totalDistance / 1000 / (this._model.distanceUnits === 'mi' ? 1.609344 : 1);
                    if (!isDragging && curDist >= markerCount + 1) {
                        const markersToAdd = Math.floor(curDist) - markerCount;
                        for (let k = 0; k < markersToAdd; k++) {
                            // kick marker number
                            markerCount++;

                            // if zoomed in far enough, show all individual mile markers
                            // if not zoomed in past 13, show every 5 if at 12
                            // if not zoomed in past 12, show every 10
                            if ((mapZoom >= 13) || ((mapZoom >= 12) && (markerCount % 5 === 0)) || ((mapZoom >= 11) && (markerCount % 10 === 0))) {
                                // find lat lng
                                const distanceToDistMarker = markerCount - lastLinePointTotalDistance;
                                let percentageDist;
                                if (this._model.distanceUnits === 'mi') {
                                    percentageDist = distanceToDistMarker / (thisLegDist / 1000 / 1.609344);
                                } else {
                                    percentageDist = distanceToDistMarker / (thisLegDist / 1000);
                                }

                                // lat lng of distance marker to place
                                const distancePointLat = prevLinePointLatLng.lat + ((thisLinePointLatLng.lat - prevLinePointLatLng.lat) * percentageDist);
                                const distancePointLng = prevLinePointLatLng.lng + ((thisLinePointLatLng.lng - prevLinePointLatLng.lng) * percentageDist);

                                var textSizeChange = '';
                                if (markerCount > 99) {
                                    textSizeChange = ' style="font-size: 9px;"';
                                }

                                // Set up icon + marker
                                const distanceMarker = new MapInterface.MarkerPoint(
                                    this.buildMarker('distance-unit-marker', {
                                        lat: distancePointLat,
                                        lng: distancePointLng
                                    }, {
                                        clickable: false,
                                        draggable: false,
                                        markerContent: '<div class="number"' + textSizeChange + '>' + markerCount + '</div><div class="dist-unit">' + this._model.distanceUnits + '</div></div>',
                                        markerImage: markerDistanceIcon
                                    }),
                                    markerCount
                                );

                                // push to distance marker array
                                this._mapModel.distanceMarkers.push(distanceMarker);

                                // draw this distance marker on map
                                distanceMarker.marker?.addTo(this._model.map);
                            }
                        }
                    }

                    // update last line point distance stamp
                    lastLinePointTotalDistance = curDist;
                }

                // draw lines on map and thumbnails in the set color
                const thisLine = {
                    type: 'Feature',
                    geometry: {
                        type: 'LineString',
                        coordinates: nextLine.map((lngLat) => [lngLat.lng, lngLat.lat])
                    }
                };

                this.updateSource(this._model.mapTrackLineName, thisLine);

                // this._mapModel.activityPolylines.push(thisLine.geometry.coordinates);

                // kick last line start point index (start at the next point, the resume)
                lastLineStartPointIndex = linePointIndex + 1;
            }
        }

        // distance to km
        this._mapModel.distance = Utils.flooredNum(totalDistance, MapInterface.COORDINATES_PRECISION) / 1000;

        // Re-apply surface color if enabled (surface data is already on linePoints)
        this.applySurfaceTypeIfEnabled();

        // run distance updated function stack
        if (this._mapModel.distanceUpdated) {
            this._mapModel.distanceUpdated(this._mapModel.distance);
        }
    };

    _removeDistanceMarkers = () => {
        // Start by removing the ghost points already there
        for (var i = 0; i < this._mapModel.distanceMarkers.length; i++) {
            this._mapModel.distanceMarkers[i].marker?.remove();
        }

        this._mapModel.distanceMarkers = [];
    };

    _drawDirectionArrows = () => {
        this._removeDirectionArrows();

        // how often SHOULD we be placing direction arrows?
        var directionArrowInterval = this.getZoomDictionary(this._model.map.getZoom()).directionArrow;
        if (!directionArrowInterval) {
            return;
        }

        // draw a fake point in between every key point to allow user to insert a new point here.
        if (this._mapModel.linePoints.length > 1) {
            // variable for distance since last direction arrow
            var distanceSinceLastDirectionArrow = 0;

            // after placing a direction arrow, how much distance is left over (so we can place correctly after the next invisible point)
            var carryoverDistance = 0;
            var lastKeyPointIndex = 0;
            for (var linePointIndex = 0; linePointIndex < this._mapModel.linePoints.length; linePointIndex++) {
                var thisLinePoint;
                var prevLinePoint;
                thisLinePoint = this._mapModel.linePoints[linePointIndex];

                // variable for point-to-point distance
                var distanceBetween = 0;
                if (linePointIndex > 0) {
                    prevLinePoint = this._mapModel.linePoints[linePointIndex - 1];
                    distanceBetween = this.distHaversine(prevLinePoint.latLng, thisLinePoint.latLng, MapInterface.COORDINATES_PRECISION);
                    distanceSinceLastDirectionArrow += distanceBetween;
                }

                // if we've traveled more than the direction arrow interval, find out how many to place
                var directionArrowsToPlace = 0;
                if (distanceSinceLastDirectionArrow > directionArrowInterval) {
                    directionArrowsToPlace = Math.floor(distanceSinceLastDirectionArrow / directionArrowInterval);
                }

                // start with carryover distance
                // move from point A to point B, placing direction arrows at interval specified (remove carryover from last loop iteration)
                // when done, set carryover distance and continue
                if (directionArrowsToPlace > 0) {
                    for (var directionArrowIndex = 1; directionArrowIndex <= directionArrowsToPlace; directionArrowIndex++) {
                        var directionArrowLat;
                        var directionArrowLng;
                        var distanceToNextDirectionArrow = (directionArrowInterval * directionArrowIndex) - carryoverDistance;
                        var percentageBetweenPoints = distanceToNextDirectionArrow / distanceBetween;

                        // we now know at what percentage distance the direction arrow should be placed. Use to put it on the line
                        directionArrowLat = prevLinePoint.latLng.lat + ((thisLinePoint.latLng.lat - prevLinePoint.latLng.lat) * percentageBetweenPoints);
                        directionArrowLng = prevLinePoint.latLng.lng + ((thisLinePoint.latLng.lng - prevLinePoint.latLng.lng) * percentageBetweenPoints);

                        var diffLat = thisLinePoint.latLng.lat - prevLinePoint.latLng.lat;
                        var diffLng = thisLinePoint.latLng.lng - prevLinePoint.latLng.lng;
                        // var center = [thisLinePoint.latLng.lat + diffLat / 2, thisLinePoint.latLng.lng + diffLng / 2];
                        var angle = (360 - ((Math.atan2(diffLat, diffLng) * 57.295779513082) % 360));

                        // create direction arrow object
                        var newDirectionArrow = new MapInterface.DirectionArrow(
                            {
                                type: 'Feature',
                                properties: {
                                    angle: angle,
                                    linePointIndex: linePointIndex,
                                    directionArrowIndex: directionArrowIndex
                                },
                                geometry: {
                                    type: 'Point',
                                    coordinates: [directionArrowLng, directionArrowLat]
                                }
                            },
                            lastKeyPointIndex
                        );

                        // add to array for reference
                        this._mapModel.directionArrows.push(newDirectionArrow);

                        if (directionArrowIndex === directionArrowsToPlace) {
                            // last need
                            carryoverDistance = distanceBetween - (percentageBetweenPoints * distanceBetween);
                            distanceSinceLastDirectionArrow = carryoverDistance;
                        }
                    }
                } else {
                    carryoverDistance += distanceBetween;
                }

                if (thisLinePoint && thisLinePoint.keyPoint !== null) {
                    lastKeyPointIndex = linePointIndex;
                }
            }

            this.updateSource('arrow-marker', {
                type: 'FeatureCollection',
                features: this._mapModel.directionArrows.map((directionArrow) => directionArrow.keyPoint)
            });
        }
    };

    _removeDirectionArrows = () => {
        if (!this._mapModel.directionArrows.length) {
            return;
        }

        this.resetMapSource('arrow-marker');

        this._mapModel.directionArrows = [];
    };

    _manageEventAction = (callback, event) => {
        event.preventDefault();
        event.originalEvent.stopPropagation();

        callback(this._convertPopupCoordinates(event), event.features[0].properties);
    };

    _onSourceEvent = (eventName, sourceIds, callback) => {
        var eventListener = this._manageEventAction.bind(this, callback);

        if (Array.isArray(sourceIds)) {
            sourceIds.forEach((sourceId) => {
                this._model.map.on(eventName, sourceId, eventListener);
            });
        } else {
            this._model.map.on(eventName, sourceIds, eventListener);
        }
    };

    _onSourceLongTouch = (sourceIds, callback) => {
        var longTouchDuration = 500;

        var touchTimeout = null;
        var clearTouchTimeout = () => {
            this._model.isLongTouching = false;
            clearTimeout(touchTimeout);
        };

        if (Array.isArray(sourceIds)) {
            sourceIds.forEach((sourceId) => {
                this._model.map.on('touchstart', sourceId, (event) => {
                    if (event.originalEvent.touches.length > 1) {
                        return;
                    }

                    this._model.isLongTouching = true;

                    var features = event.features;
                    touchTimeout = setTimeout(function (touchEvent) {
                        touchEvent.features = features;
                        this._manageEventAction(callback, touchEvent);
                    }.bind(this, event), longTouchDuration);
                });

                this._model.map.on('touchend', sourceId, clearTouchTimeout);
                this._model.map.on('touchcancel', sourceId, clearTouchTimeout);
                this._model.map.on('touchmove', sourceId, clearTouchTimeout);
            });
        } else {
            this._model.map.on('touchstart', sourceIds, (event) => {
                if (event.originalEvent.touches.length > 1) {
                    return;
                }

                this._model.isLongTouching = true;

                var features = event.features;
                touchTimeout = setTimeout(function (touchEvent) {
                    touchEvent.features = features;
                    this._manageEventAction(callback, touchEvent);
                }.bind(this, event), longTouchDuration);
            });

            this._model.map.on('touchend', sourceIds, clearTouchTimeout);
            this._model.map.on('touchcancel', sourceIds, clearTouchTimeout);
            this._model.map.on('touchmove', sourceIds, clearTouchTimeout);
        }
    };

    _onDragSource = (sourceId, onDragStart, onDragging, onDragEnd, markerDragProperties) => {
        const canvas = this._model.map.getCanvasContainer();
        let markerProperties;
        markerDragProperties ||= {};

        const onDragMove = (event) => {
            var coordinates = event.lngLat;

            canvas.style.cursor = 'grabbing';

            onDragging(sourceId, coordinates, markerProperties);
        };

        const onDragUp = (event) => {
            var coordinates = event.lngLat;

            canvas.style.cursor = '';

            onDragEnd(sourceId, coordinates, markerProperties);

            // Unbind mouse/touch events
            this._model.map.off('mousemove', onDragMove);
            this._model.map.off('touchmove', onDragMove);
        };

        // When the cursor enters a feature in the point layer, prepare for dragging.
        this._model.map.on('mouseenter', sourceId, () => {
            this._model.map.setPaintProperty(sourceId, 'icon-opacity', typeof markerDragProperties.hoverOpacity === 'undefined' ? 1 : markerDragProperties.hoverOpacity);

            canvas.style.cursor = 'move';
        });

        this._model.map.on('mouseleave', sourceId, () => {
            if (!this._model.map) {
                return;
            }

            this._model.map.setPaintProperty(sourceId, 'icon-opacity', typeof markerDragProperties.defaultOpacity === 'undefined' ? 1 : markerDragProperties.defaultOpacity);

            canvas.style.cursor = '';
        });

        this._model.map.on('mousedown', sourceId, (event) => {
            // Prevent move if not left click
            if (event.originalEvent.button !== 0 || this._model.isLongTouching) {
                return;
            }

            // Prevent the default map drag behavior
            event.preventDefault();
            event.originalEvent.stopPropagation();

            canvas.style.cursor = 'grab';

            markerProperties = event.features[0].properties;
            onDragStart(sourceId, event.lngLat, markerProperties);
            this._model.map.on('mousemove', onDragMove);
            this._model.map.once('mouseup', onDragUp);
        });

        this._model.map.on('touchstart', sourceId, (event) => {
            if (event.points.length !== 1 || this._model.isLongTouching) {
                return;
            }

            // Prevent the default map drag behavior.
            event.preventDefault();
            event.originalEvent.stopPropagation();

            markerProperties = event.features[0].properties;
            onDragStart(sourceId, event.lngLat, markerProperties);
            this._model.map.on('touchmove', onDragMove);
            this._model.map.once('touchend', onDragUp);
        });
    };

    _addKeyPointDebounce = Utils.debounce((event) => {
        if (this._model.isDoubleClick) {
            this._model.isDoubleClick = false;
            return;
        }

        // Avoid double click
        this._addKeyPointAtClickLoc(event.lngLat);
    }, 200);

    _addKeyPointEventHandlers = () => {
        this._onSourceEvent('click', ['start-marker', 'step-marker', 'finish-marker'], (coordinates, markerProperties) => {
            this._addLinePoint(coordinates, markerProperties);
        });

        this._onSourceEvent('contextmenu', ['start-marker', 'step-marker', 'finish-marker'], (coordinates, markerProperties) => {
            this._removeLinePoint(coordinates, markerProperties);
        });

        this._onSourceLongTouch(['start-marker', 'step-marker', 'finish-marker'], (coordinates, markerProperties) => {
            this._removeLinePoint(coordinates, markerProperties);
        });

        ['start-marker', 'step-marker', 'finish-marker'].forEach((markerId) => {
            this._onDragSource(
                markerId,
                this._onMarkerDragStart.bind(this),
                this._onMarkerDragging.bind(this),
                this._onMarkerDragEnd.bind(this)
            );
        });

        // this.onDragSource(
        //     'ghost-marker',
        //     this._onGhostPointDragStart.bind(this),
        //     this._onGhostPointDragging.bind(this),
        //     this._onGhostPointDragEnd.bind(this),
        //     {
        //         defaultOpacity: 0,
        //         hoverOpacity: 0.7
        //     }
        // );
    };

    _addLinePoint = (coordinates, markerProperties) => {
        if (typeof markerProperties.linePointIndex === 'undefined') {
            return;
        }

        // Merge start and end point if click on first point
        if (markerProperties.linePointIndex !== 0
            // OPTIMIZE: for edit mode
            // || (this._model.mode !== localMap.mode.NEW && this._model.mode !== localMap.mode.EDIT)
        ) {
            return;
        }

        if (this._mapModel.editMode) {
            return;
        }

        this._addKeyPointAtClickLoc(coordinates);
    };

    _removeLinePoint = (coordinates, markerProperties) => {
        this._pushUndoSnapShot();

        var linePointIndex = markerProperties.linePointIndex;
        markerProperties.prevKeyPointIndex = this._getPrevKeyPointIndex(linePointIndex, true);
        markerProperties.nextKeyPointIndex = this._getNextKeyPointIndex(linePointIndex, true);
        var deleteCurrentPointAsWell = (linePointIndex === markerProperties.nextKeyPointIndex || linePointIndex === markerProperties.prevKeyPointIndex);

        this._removeLinePointsBetween(markerProperties.prevKeyPointIndex, markerProperties.nextKeyPointIndex);
        var isFirstPoint = markerProperties.markerId === 'start-marker' || (linePointIndex === 0);
        var isLastPoint = markerProperties.markerId === 'finish-marker' || (this._mapModel.linePoints.length - 1 === linePointIndex);

        linePointIndex -= (linePointIndex - markerProperties.prevKeyPointIndex);

        if (deleteCurrentPointAsWell) {
            if (isFirstPoint) {
                this._mapModel.linePoints.splice(0, 1);
            } else if (isLastPoint) {
                this._mapModel.linePoints.splice(this._mapModel.linePoints.length - 1, 1);
            } else {
                this._mapModel.linePoints.splice(linePointIndex, 1);
            }
        }

        markerProperties.linePointIndex = linePointIndex;

        this._redrawKeyPoints();

        if (!this._mapModel.linePoints.length) {
            this.resetMapSource('start-marker');
        } else if (this._mapModel.linePoints.length === 1) {
            this._redrawTripLine();
        }

        this._mapEditedFuncs();

        if (this.hasModule('RIDE_STORE') && this.storeRide) {
            this.storeRide(this._mapModel.linePoints);
        }
    };

    _removeLinePointsBetween = (startIndex, endIndex) => {
        if (startIndex === null || startIndex === undefined || typeof startIndex === 'undefined' ||
            endIndex === null || endIndex === undefined || typeof endIndex === 'undefined') {
            return [];
        }
        var totalToDelete = endIndex - startIndex - 1;
        var deletedPoints = this._mapModel.linePoints.splice(startIndex + 1, totalToDelete);
        return deletedPoints;
    };

    _onMarkerDragStart = (markerId, coordinates, markerProperties) => {
        // Prevent double dragging
        if (this._mapModel.draggingMarkerId && this._mapModel.draggingMarkerId !== markerId) {
            return;
        }
        this._mapModel.draggingMarkerId = markerId;
        this._model.previousRoadComputedPoints = 0;

        this._pushUndoSnapShot();

        // remove direction arrows
        this._removeDirectionArrows();

        // event handlers for drag start, drag end, drag
        var linePointIndex = markerProperties.linePointIndex;
        // get next keypoint line point index, previous keypoint line point index
        markerProperties.prevKeyPointIndex = this._getPrevKeyPointIndex(linePointIndex);
        markerProperties.nextKeyPointIndex = this._getNextKeyPointIndex(linePointIndex);

        if (markerId !== 'start-marker') {
            var deletedPointsBefore;
            var deletedPointsAfter;

            if (markerProperties.prevKeyPointIndex !== null) {
                // delete points before
                deletedPointsBefore = this._removeLinePointsBetween(markerProperties.prevKeyPointIndex, linePointIndex);
                // points have been removed, adjust index references
                linePointIndex -= deletedPointsBefore.length;
                markerProperties.nextKeyPointIndex -= deletedPointsBefore.length;
            }

            if (markerProperties.nextKeyPointIndex !== null) {
                // delete points after
                deletedPointsAfter = this._removeLinePointsBetween(linePointIndex, markerProperties.nextKeyPointIndex);
                // points have been removed, adjust index references
                markerProperties.nextKeyPointIndex -= deletedPointsAfter.length;
            }
        }

        markerProperties.linePointIndex = linePointIndex;

        this.resetMapSource('step-marker');
    };

    _onMarkerDragging = (markerId, coordinates, markerProperties) => {
        // Prevent double dragging
        if (this._mapModel.draggingMarkerId && this._mapModel.draggingMarkerId !== markerId) {
            return;
        }

        var linePointIndex = markerProperties.linePointIndex;
        var thisLinePoint = this._mapModel.linePoints[linePointIndex];
        if (!thisLinePoint) {
            return;
        }

        thisLinePoint.computed = false;

        thisLinePoint.latLng = coordinates;

        if (markerId !== 'step-marker' && this._model.snapToRoads && this.isMapPlannerRouting(this._model.routingService)) {
            var startingPoint;
            var endingPoint;

            if (markerId === 'start-marker') {
                startingPoint = thisLinePoint;
                if (this._mapModel.linePoints.length > 1) {
                    endingPoint = this._mapModel.linePoints[linePointIndex + 1 + this._model.previousRoadComputedPoints];
                }
            } else if (markerId === 'finish-marker') {
                startingPoint = this._mapModel.linePoints[linePointIndex - 1];
                endingPoint = thisLinePoint;
            }

            if (startingPoint && endingPoint) {
                this.getSnapToRoadsRoute(
                    startingPoint.latLng,
                    endingPoint.latLng,
                    null,
                    (pointArray) => {
                        if (typeof pointArray !== 'undefined') {
                            var points = [];
                            for (var routePointIndex = 0; routePointIndex < pointArray.length; routePointIndex++) {
                                points.push(new MapInterface.PointInfo(pointArray[routePointIndex], null, {computed: true, surface: pointArray[routePointIndex].surface}));
                            }

                            if (markerId === 'start-marker') {
                                Array.prototype.splice.apply(this._mapModel.linePoints, [0, this._model.previousRoadComputedPoints || 1].concat(points));
                            } else {
                                Array.prototype.splice.apply(this._mapModel.linePoints, [linePointIndex, this._model.previousRoadComputedPoints + 1].concat(points));
                            }

                            this._model.previousRoadComputedPoints = points.length;
                        }

                        this._redrawKeyPoints();
                    },
                    {
                        limitDistance: true,
                        distance: this._mapModel.distance
                    }
                );
            } else {
                this._redrawKeyPoints();
            }
        } else {
            this._redrawTripLine();

            // Update collection with only one point when dragging, display all points when dragging end
            this.updateSource(markerId, {
                type: 'FeatureCollection',
                features: [
                    {
                        type: 'Feature',
                        properties: markerProperties,
                        geometry: {
                            type: 'Point',
                            coordinates: [thisLinePoint.latLng.lng, thisLinePoint.latLng.lat]
                        }
                    }
                ]
            });
        }
    };

    _onMarkerDragEnd = (markerId, coordinates, markerProperties) => {
        // Prevent double dragging
        if (this._mapModel.draggingMarkerId && this._mapModel.draggingMarkerId !== markerId) {
            return;
        }
        this._mapModel.draggingMarkerId = null;
        this._model.previousRoadComputedPoints = 0;

        var linePointIndex = markerProperties.linePointIndex;
        var thisLinePoint = this._mapModel.linePoints[linePointIndex];
        if (!thisLinePoint) {
            return;
        }

        if (markerId === 'start-marker') {
            if (this._mapModel.startPointChanged) {
                this._mapModel.startPointChanged(coordinates);
            }
        }

        // we are either already set, or need to fill with snap to roads points
        if ((this.isMapPlannerRouting(this._model.routingService) ? markerId === 'step-marker' : true) && this._model.snapToRoads && this._mapModel.linePoints.length > 1 && thisLinePoint) {
            // nothing before starting point / nothing before a resume point
            // nothing after end point / nothing after a pause point
            // otherwise, use this point as waypoint

            var startingPoint;
            var endingPoint;
            var waypoint;
            var callback;
            if (linePointIndex === 0 || thisLinePoint.deltaPause > 0) {
                // this is a start or resume point, only show stuff after
                startingPoint = thisLinePoint.latLng;
                if (this._mapModel.linePoints[markerProperties.nextKeyPointIndex]) {
                    endingPoint = this._mapModel.linePoints[markerProperties.nextKeyPointIndex].latLng;
                }
                waypoint = null;
                callback = (pointArray) => {
                    if (typeof pointArray !== 'undefined') {
                        var points = [];
                        for (var routePointIndex = 0; routePointIndex < pointArray.length; routePointIndex++) {
                            points.push(new MapInterface.PointInfo(pointArray[routePointIndex], null, {computed: true, surface: pointArray[routePointIndex].surface}));
                        }

                        for (var j = 0; j < points.length; j++) {
                            this._mapModel.linePoints.splice(linePointIndex + 1 + j, 0, points[j]);
                        }
                    }

                    this._redrawKeyPoints();
                    this._mapEditedFuncs();

                    if (this.hasModule('ISOCHRONE') && linePointIndex === 0) {
                        this.computeIsochrone(coordinates);
                    }
                };
            } else if (linePointIndex === this._mapModel.linePoints.length - 1 ||
                this._mapModel.linePoints[linePointIndex + 1].deltaPause > 0) {
                // this is a finish or pause point, only show stuff before
                if (this._mapModel.linePoints[markerProperties.prevKeyPointIndex]) {
                    startingPoint = this._mapModel.linePoints[markerProperties.prevKeyPointIndex].latLng;
                }
                endingPoint = thisLinePoint.latLng;
                waypoint = null;
                callback = (pointArray) => {
                    if (typeof pointArray !== 'undefined') {
                        var points = [];
                        for (var n = 0; n < pointArray.length; n++) {
                            points.push(new MapInterface.PointInfo(pointArray[n], null, {computed: true, surface: pointArray[n].surface}));
                        }

                        for (var j = 0; j < points.length; j++) {
                            this._mapModel.linePoints.splice(markerProperties.prevKeyPointIndex + 1 + j, 0, points[j]);
                        }
                    }

                    markerProperties.linePointIndex = linePointIndex + 1;

                    this._redrawKeyPoints();
                    this._mapEditedFuncs();
                };
            } else {
                // normal, move with waypoint
                if (this._mapModel.linePoints[markerProperties.prevKeyPointIndex]) {
                    startingPoint = this._mapModel.linePoints[markerProperties.prevKeyPointIndex].latLng;
                }
                if (this._mapModel.linePoints[markerProperties.nextKeyPointIndex]) {
                    endingPoint = this._mapModel.linePoints[markerProperties.nextKeyPointIndex].latLng;
                }
                waypoint = thisLinePoint.latLng;
                callback = (pointArray) => {
                    if (typeof pointArray !== 'undefined') {
                        var createdReplacementPoint = false;
                        var wasCreatedReplacementPoint = false;
                        for (var routePointIndex = 0; routePointIndex < pointArray.length; routePointIndex++) {
                            var numberToOverwrite = 0;
                            var newKeypoint = null;
                            var newLinePointIndex = markerProperties.prevKeyPointIndex + 1 + routePointIndex;
                            if (routePointIndex > (pointArray.length / 2) && !createdReplacementPoint) {
                                newKeypoint = {
                                    type: 'Feature',
                                    properties: {
                                        linePointIndex: newLinePointIndex,
                                        draggable: this._mapModel.editMode
                                    },
                                    geometry: {
                                        type: 'Point',
                                        coordinates: [pointArray[routePointIndex].lng, pointArray[routePointIndex].lat]
                                    }
                                };

                                createdReplacementPoint = true;
                            }
                            if (routePointIndex === pointArray.length - 1) {
                                numberToOverwrite = 1;
                                // newLinePointIndex marker automatically removed on next redraw
                            }
                            var isComputed = true;
                            if (createdReplacementPoint && !wasCreatedReplacementPoint) {
                                isComputed = false;
                                wasCreatedReplacementPoint = true;
                            }
                            this._mapModel.linePoints.splice(newLinePointIndex, numberToOverwrite, new MapInterface.PointInfo(pointArray[routePointIndex], newKeypoint, {computed: isComputed, surface: pointArray[routePointIndex].surface}));
                        }
                    }

                    this._redrawKeyPoints();
                    this._mapEditedFuncs();
                };
            }

            this.getSnapToRoadsRoute(
                startingPoint,
                endingPoint,
                waypoint,
                callback,
                {
                    limitDistance: true,
                    distance: this._mapModel.distance
                }
            );
        } else {
            this._redrawKeyPoints();
            this._mapEditedFuncs();
        }
    };

    _getNextKeyPointIndex = (fromIndex, ignoreComputed) => {
        // discover index of next keypoint
        for (var linePointIndex = fromIndex + 1; linePointIndex < this._mapModel.linePoints.length; linePointIndex++) {
            var thisLinePoint = this._mapModel.linePoints[linePointIndex];
            if (thisLinePoint && (ignoreComputed || !thisLinePoint.computed)) {
                return linePointIndex;
            }
        }

        return fromIndex;
    };

    _getPrevKeyPointIndex = (fromIndex, ignoreComputed) => {
        // discover index of next keypoint
        if (fromIndex !== 0) {
            for (var linePointIndex = fromIndex - 1; linePointIndex >= 0; linePointIndex--) {
                var thisLinePoint = this._mapModel.linePoints[linePointIndex];
                if (thisLinePoint && (ignoreComputed || !thisLinePoint.computed)) {
                    return linePointIndex;
                }
            }
        }

        return fromIndex;
    };

    _clearKeyPoints = (options) => {
        options ||= {};

        for (var linePointIndex = 0; linePointIndex < this._mapModel.linePoints.length; linePointIndex++) {
            if (options.keepStartStop) {
                if (linePointIndex === 0 || linePointIndex === this._mapModel.linePoints.length - 1) {
                    continue;
                }
            }

            var thisLinePoint = this._mapModel.linePoints[linePointIndex];
            if (thisLinePoint && thisLinePoint.keyPoint !== null) {
                thisLinePoint.keyPoint = null;
            }
        }

        if (options.clearAll) {
            this.resetMapSource('arrow-marker');
            this.resetMapSource('ghost-marker');
            this.resetMapSource('step-marker');
            this.resetMapSource('pause-marker');
            this.resetMapSource('resume-marker');

            this.resetMapSource('start-marker');
            this.resetMapSource('finish-marker');
        }
    };

    _addKeyPointAtClickLoc = (coordinates) => {
        // Prevent bubbling click on marker and on map
        if (this._mapModel.markerEventClicked) {
            this._mapModel.markerEventClicked = false;
            return;
        }

        if (this._model.searchMarker) {
            this._model.searchMarker.remove();
        }

        if (this._mapModel.hasPopup) {
            this._mapModel.hasPopup = false;
            return;
        }

        // ensure that we aren't in the process of dragging a point or computing a path
        if (this._model.isComputingRoad) {
            return;
        }

        // Setup a snapshot to revert to
        this._pushUndoSnapShot();

        // create new point, add to map, redraw the trip line

        // we don't want to use the routing api if it's only the first point on the map (there's no beginning / end)
        var firstPoint = false;
        if (this._mapModel.linePoints.length === 0) {
            firstPoint = true;
        }
        var secondPoint = false;
        if (this._mapModel.linePoints.length === 1) {
            secondPoint = true;
        }

        if (firstPoint) {
            if (this._mapModel.startPointChanged) {
                this._mapModel.startPointChanged(coordinates);
            }
        }

        if (this.hasModule('TOOLBAR') && this.activateToolbarButtons) {
            if (firstPoint) {
                this.activateToolbarButtons();
            } else {
                this.activateBackToHome();
            }
        }

        if (this.hasModule('ISOCHRONE') && this.computeIsochrone && firstPoint) {
            this.computeIsochrone(coordinates);
        }

        if (this.hasModule('HELPER') && this.plugHelperEvents) {
            if (firstPoint) {
                this.displayMapHelper(window.screen.width < 1024
                        ?
                        I18n.t('js.maps.helpers.mobile.draw_route')
                        :
                        I18n.t('js.maps.helpers.desktop.draw_route')
                    , {limitDisplay: true});
            } else if (secondPoint) {
                this.displayMapHelper(window.screen.width < 1024
                        ?
                        I18n.t('js.maps.helpers.mobile.remove_point')
                        :
                        I18n.t('js.maps.helpers.desktop.remove_point')
                    , {limitDisplay: true});
            } else {
                this.hideHelpers();
            }
        }

        // need to remember where in the linePoints array we're adding to, so we can prep the undo function
        // var prevEndingIndex = this._mapModel.linePoints.length;

        // create any points involved, add to linePoints array
        if (this._model.snapToRoads && !firstPoint) {
            this.getSnapToRoadsRoute(
                this._mapModel.linePoints[this._mapModel.linePoints.length - 1].latLng,
                coordinates,
                null,
                (pointArray) => {
                    if (Array.isArray(pointArray)) {
                        for (let pointIndex = 0; pointIndex < pointArray.length; pointIndex++) {
                            this._mapModel.linePoints.push(new MapInterface.PointInfo(pointArray[pointIndex], null, {computed: pointArray.length - 1 !== pointIndex, surface: pointArray[pointIndex].surface}));
                        }
                    } else {
                        // Error: just display a direct line
                        this._mapModel.linePoints.push(new MapInterface.PointInfo(coordinates, null, {computed: false}));
                    }

                    this._redrawKeyPoints();
                    this._mapEditedFuncs();

                    if (this.hasModule('RIDE_STORE') && this.storeRide) {
                        this.storeRide(this._mapModel.linePoints);
                    }
                },
                {
                    limitDistance: true,
                    distance: this._mapModel.distance
                }
            );
        } else {
            this._mapModel.linePoints.push(new MapInterface.PointInfo({
                lat: parseFloat(coordinates.lat.toFixed(MapInterface.COORDINATES_PRECISION)),
                lng: parseFloat(coordinates.lng.toFixed(MapInterface.COORDINATES_PRECISION))
            }, null, {computed: false}));
            this._redrawKeyPoints();
            this._mapEditedFuncs();

            if (this.hasModule('RIDE_STORE') && this.storeRide) {
                this.storeRide(this._mapModel.linePoints);
            }
        }
    };

    _pushUndoSnapShot = () => {
        var i;

        var snapShot = [];
        for (i = 0; i < this._mapModel.linePoints.length; i++) {
            var thisLinePoint = this._mapModel.linePoints[i];
            snapShot.push(new MapInterface.PointInfo(
                thisLinePoint.latLng,
                null,
                {
                    computed: thisLinePoint.computed,
                    deltaDistance: thisLinePoint.deltaDistance,
                    deltaTime: thisLinePoint.deltaTime,
                    deltaPause: thisLinePoint.deltaPause,
                    datetime: thisLinePoint.datetime,
                    elevation: thisLinePoint.elevation,
                    speed: thisLinePoint.speed,
                    heartRate: thisLinePoint.heartRate
                }
            ));
        }
        this._mapModel.snapShotStack.push(snapShot);
    };

    _revertToSnapshot = (snapShotIndex) => {
        var i;
        var numberToPop;

        if (typeof snapShotIndex === 'undefined' || snapShotIndex === undefined || snapShotIndex === null) {
            snapShotIndex = this._mapModel.snapShotStack.length - 1;
        }

        if (snapShotIndex < 0) {
            return;
        }

        this._clearKeyPoints({clearAll: true});

        this._mapModel.linePoints = this._mapModel.snapShotStack[snapShotIndex] || [];

        numberToPop = this._mapModel.snapShotStack.length - snapShotIndex;
        for (i = 0; i < numberToPop; i++) {
            this._mapModel.snapShotStack.pop();
        }

        this._redrawKeyPoints();

        if (!this._mapModel.linePoints.length) {
            this.resetMapSource('start-marker');
        } else if (this._mapModel.linePoints.length === 1) {
            this._redrawTripLine();
        }

        this._mapEditedFuncs();
    };

    _mapEditedFuncs = () => {
        for (var i = 0, n = this._mapModel.mapEdited.length; i < n; i++) {
            this._mapModel.mapEdited[i](); //let callback know
        }
    };

    _fitMapBounds = (options = {}) => {
        if (!this._model.map) {
            return;
        }

        // get the map bounds by looking at all polylines involved
        if (this._model.mapBounds !== null) {
            return;
        }

        var bounds = this._buildBounds();

        // Fit to polylines or clusters
        if (this._mapModel.linePoints.length > 0) {
            this._mapModel.linePoints.forEach((point) => {
                bounds = bounds.extend([point.latLng.lng, point.latLng.lat]);
            });
        }

        if (Utils.isPresent(this._model.includeBounds) && this._model.includeBounds.length === 4) {
            var bbox = [
                [
                    parseFloat(this._model.includeBounds[1]),
                    parseFloat(this._model.includeBounds[0])
                ],
                [
                    parseFloat(this._model.includeBounds[3]),
                    parseFloat(this._model.includeBounds[2])
                ]
            ];
            bounds = bounds.extend(bbox);
        }

        if (this.hasModule('RIDE_POIS') && this.fitRidePoisBounds) {
            this.fitRidePoisBounds(bounds);
        }

        // fit to the overall bounds
        var fitBoundsOptions = {
            // Prevent map zoom on initial map loading
            zoom: this._model.minZoom,
            padding: this._mapModel.initialPoints || options.withPois ? 150 : 20
        };

        this._model.mapBounds = bounds;
        this.fitBounds(this._model.mapBounds, fitBoundsOptions);

        // Set minimum zoom
        if (this._model.map.getZoom() > (this._model.minZoom || 16)) {
            this._model.map.setZoom(this._model.minZoom || 16);
        }
    };

    // Called by MapInterface
    _onMapStyleChanged = () => {
        this.addMapTrackLine(this._model.mapTrackLineName);

        // this._redrawTripLine();

        this._redrawKeyPoints();

        // this._drawDirectionArrows();
    };
}
